class PbfcmComparisonExtended extends HTMLElement {
  static TRANSITION_MS = 400;
  static MOBILE_MAX_WIDTH = 599;

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-comparison-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-comparison-panel]"));
    this.viewport = this.querySelector("[data-comparison-panels-viewport]");
    this.select = this.querySelector("[data-comparison-select]");
    this.activeTabId = null;
    this.isAnimating = false;

    this.onClick = this.onClick.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.addEventListener("click", this.onClick);
    this.select?.addEventListener("change", this.onSelectChange);

    const initial =
      this.tabs.find((tab) => tab.classList.contains("fd-tabs-v3__item--active")) ||
      this.tabs[0];

    if (initial) {
      this.panels.forEach((panel) => panel.removeAttribute("hidden"));
      this.activate(initial.dataset.comparisonTab, { immediate: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.select?.removeEventListener("change", this.onSelectChange);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  isMobileView() {
    return window.matchMedia(
      `(max-width: ${PbfcmComparisonExtended.MOBILE_MAX_WIDTH}px)`,
    ).matches;
  }

  onClick(event) {
    const tab = event.target.closest("[data-comparison-tab]");
    if (!tab || !this.contains(tab)) return;
    this.activate(tab.dataset.comparisonTab);
  }

  onSelectChange() {
    if (!this.select) return;
    this.activate(this.select.value);
  }

  syncSelectValue(tabId) {
    if (!this.select || this.select.value === tabId) return;
    this.select.value = tabId;
  }

  getActivePanel() {
    return this.panels.find(
      (panel) => panel.dataset.comparisonPanel === this.activeTabId,
    );
  }

  measurePanel(panel) {
    if (!panel || !this.viewport) return 0;

    const wasHidden = panel.hidden;
    panel.hidden = false;
    panel.classList.add("is-measuring");
    const height = panel.offsetHeight;
    panel.classList.remove("is-measuring");
    panel.hidden = wasHidden;
    return height;
  }

  syncViewportHeight(panel = this.getActivePanel()) {
    if (!this.viewport || !panel) return;
    this.viewport.style.height = `${this.measurePanel(panel)}px`;
  }

  activate(tabId, { immediate = false } = {}) {
    if (!tabId || tabId === this.activeTabId) return;

    const nextPanel = this.panels.find(
      (panel) => panel.dataset.comparisonPanel === tabId,
    );
    const currentPanel = this.getActivePanel();
    if (!nextPanel) return;

    this.tabs.forEach((tab) => {
      const active = tab.dataset.comparisonTab === tabId;
      tab.classList.toggle("fd-tabs-v3__item--active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
    this.syncSelectValue(tabId);

    const useImmediate =
      immediate || !this.viewport || !currentPanel || this.isMobileView();

    if (useImmediate) {
      this.panels.forEach((panel) => {
        const isActive = panel === nextPanel;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        panel.setAttribute("aria-hidden", isActive ? "false" : "true");
        panel.classList.remove("is-fading-in", "is-fading-out");
      });
      this.activeTabId = tabId;
      this.syncViewportHeight(nextPanel);
      return;
    }

    if (this.isAnimating) return;
    this.isAnimating = true;
    this.viewport.classList.add("is-transitioning");

    const currentHeight = this.viewport.offsetHeight;
    const nextHeight = this.measurePanel(nextPanel);

    nextPanel.hidden = false;
    nextPanel.setAttribute("aria-hidden", "false");
    nextPanel.classList.add("is-active", "is-fading-in");

    currentPanel.classList.remove("is-active");
    currentPanel.classList.add("is-fading-out");

    this.viewport.style.height = `${currentHeight}px`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.viewport.style.height = `${nextHeight}px`;
        nextPanel.classList.remove("is-fading-in");
      });
    });

    window.setTimeout(() => {
      this.panels.forEach((panel) => {
        const isActive = panel === nextPanel;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        panel.setAttribute("aria-hidden", isActive ? "false" : "true");
        panel.classList.remove("is-fading-in", "is-fading-out", "is-measuring");
      });

      this.viewport.classList.remove("is-transitioning");
      this.viewport.style.height = `${nextHeight}px`;
      this.activeTabId = tabId;
      this.isAnimating = false;
    }, PbfcmComparisonExtended.TRANSITION_MS);
  }
}

customElements.define("pbfcm-comparison-extended", PbfcmComparisonExtended);
